import React, { useEffect, useState } from "react";

import { API_BASE_URL } from "../config";

import "../styles/assing.scss";
import Loader from "../components/Loader";
import Toast from "../components/Toast";
import {
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Box,
  Chip,
  Typography,
} from "@mui/material";

function Assign() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({
    visible: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/admin/users`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setUsers(data));

    fetch(`${API_BASE_URL}/api/admin/projects`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setProjects(data));
  }, []);

  const handleUserChange = async (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);

    if (!userId) {
      setAssignedProjects([]);
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/projects`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
        }
      );

      const data = await res.json();
      setAssignedProjects(data.map((proj) => proj.id));
    } catch (err) {
      console.error("Failed to fetch user's projects:", err);
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    setLoading(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/users/${selectedUser}/assign-projects`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ projectIds: assignedProjects }),
          credentials: "include",
        }
      );

      setTimeout(() => {
        setLoading(false);

        if (res.ok) {
          showToast("Assignments updated successfully!", "success");
        } else {
          showToast("Failed to update assignments", "error");
        }
      }, 2000);
    } catch (error) {
      setTimeout(() => {
        setLoading(false);
        alert("An error occurred while assigning projects.");
        console.error("Assignment error:", error);
      }, 2000);
    }
  };

  return (
    <>
      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, visible: false })}
      />

      <div
        className="assign-page"
        style={{ background: "#fff", padding: 10, borderRadius: 10 }}
      >
        <Box className="header">
          <Typography variant="h5" fontWeight={500}>
            Assign Projects
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
          Select a user and assign one or more projects to them. You can also
          remove all assigned projects using the "Clear" button.
        </Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel>Select User</InputLabel>
          <Select
            style={{ borderRadius: 30 }}
            labelId="select-user-label"
            value={selectedUser}
            onChange={handleUserChange}
            input={<OutlinedInput label="Select User" />}
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Assign Projects</InputLabel>
          <Select
            labelId="assign-projects-label"
            multiple
            style={{ borderRadius: 30 }}
            value={assignedProjects}
            onChange={(e) => setAssignedProjects(e.target.value)}
            input={<OutlinedInput label="Assign Projects" />}
            renderValue={(selected) => (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {selected.map((value) => {
                  const project = projects.find((p) => p.id === value);
                  return <Chip key={value} label={project?.name || value} />;
                })}
                {selected.length > 0 && (
                  <Button
                    onMouseDown={(e) => {
                      e.stopPropagation(); // Prevent Select from opening
                    }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent Select from opening
                      setAssignedProjects([]);
                      if (selectedUser) {
                        fetch(
                          `${API_BASE_URL}/api/admin/users/${selectedUser}/assign-projects`,
                          {
                            method: "PUT",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${localStorage.getItem(
                                "token"
                              )}`,
                            },
                            body: JSON.stringify({ projectIds: [] }),
                            credentials: "include",
                          }
                        )
                          .then((res) => {
                            if (res.ok) {
                              showToast(
                                "Projects cleared successfully!",
                                "success"
                              );
                            } else {
                              showToast("Failed to clear projects", "error");
                            }
                          })
                          .catch((error) => {
                            console.error("Clear projects error:", error);
                            showToast("An error occurred", "error");
                          });
                      }
                    }}
                    size="small"
                    color="error"
                    sx={{
                      ml: 1,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      padding: "2px 6px",
                      minWidth: "unset",
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            )}
          >
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <br />
        <br />
        <Button
          variant="contained"
          style={{
            background: "var(--gradient-color)",
            borderRadius: 30,
            padding: "15px 20px",
          }}
          onClick={handleSave}
          disabled={!selectedUser || loading}
        >
          {loading ? <Loader size="20px" /> : "Save Assignments"}
        </Button>
      </div>
    </>
  );
}

export default Assign;
